import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Ticket, 
  CheckCircle, 
  Target, 
  Star,
  Activity,
  Users,
  Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { ticketService } from '../../api/ticketService';
import authService, { getCurrentUser } from '../../api/authService';

/** Normalize API ticket for read-only dashboard list (aligned with customer portal modal). */
const mapAgentTicketRow = (ticket) => {
  let status = 'open';
  if (ticket.status) {
    if (typeof ticket.status === 'string') {
      status = ticket.status.toLowerCase();
    } else if (ticket.status.statusName) {
      status = String(ticket.status.statusName).toLowerCase();
    } else if (ticket.status.statusCode) {
      status = String(ticket.status.statusCode).toLowerCase();
    }
  }
  const priorityRaw = ticket.priorityName || ticket.priority || 'medium';
  const priority = typeof priorityRaw === 'string' ? priorityRaw.toLowerCase() : 'medium';
  return {
    id: ticket.ticketId || ticket.ticketNumber,
    ticketNumber: ticket.ticketNumber || ticket.ticketId || ticket.id,
    subject: ticket.subject,
    title: ticket.subject,
    description: ticket.description,
    status,
    priority,
    priorityLabel: ticket.priorityName || ticket.priority || 'medium',
    createdAt: ticket.createdAt,
    updatedAt: ticket.updatedAt,
    resolvedAt: ticket.resolvedAt,
    lastUpdate: ticket.updatedAt || ticket.createdAt,
    assignedTo:
      ticket.assigneeFullName ||
      (ticket.assigneeId != null ? `Agent ${ticket.assigneeId}` : 'Unassigned')
  };
};

const modalStatusBadgeClass = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'open':
    case 'new':
      return 'bg-blue-100 text-blue-800';
    case 'in-progress':
    case 'in progress':
    case 'assigned':
      return 'bg-yellow-100 text-yellow-800';
    case 'resolved':
      return 'bg-green-100 text-green-800';
    case 'closed':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const modalPriorityBadgeClass = (priority) => {
  switch ((priority || '').toLowerCase()) {
    case 'critical':
      return 'bg-red-100 text-red-800';
    case 'urgent':
      return 'bg-red-800 text-red-50';
    case 'high':
      return 'bg-orange-100 text-orange-800';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800';
    case 'low':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const formatModalDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const StatValue = ({ loading, children, className = '' }) => {
  if (loading) {
    return (
      <span
        className={`inline-block h-8 w-14 rounded bg-gray-200 dark:bg-gray-700 animate-pulse ${className}`}
        aria-hidden
      />
    );
  }
  return children;
};

const AgentDashboard = () => {
  const navigate = useNavigate();
  const [statsLoading, setStatsLoading] = useState(true);
  const [ratingLoading, setRatingLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [teamOverviewLoading, setTeamOverviewLoading] = useState(false);
  const [myTicketsCount, setMyTicketsCount] = useState(0);
  const [customerRating, setCustomerRating] = useState(null);
  const [teamOverview, setTeamOverview] = useState(null);
  const [ticketStats, setTicketStats] = useState({
    open: 0,
    pending: 0,
    escalated: 0,
    resolved: 0,
    inProgress: 0,
    resolvedToday: 0,
    totalResolved: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const fetchMyTicketsCountRef = useRef(null);
  const fetchRecentActivityRef = useRef(null);
  const fetchTeamOverviewRef = useRef(null);

  const [displayRoleName, setDisplayRoleName] = useState(() => authService.getStoredUserRoleName());

  /** 'myTickets' | 'resolvedToday' | null — read-only ticket list modal */
  const [ticketListModal, setTicketListModal] = useState(null);
  const [modalTickets, setModalTickets] = useState([]);
  const [modalTicketsLoading, setModalTicketsLoading] = useState(false);
  const [ticketModalSearchQuery, setTicketModalSearchQuery] = useState('');
  const [ticketModalStatusFilter, setTicketModalStatusFilter] = useState('all');

  // Check if user is Team Lead
  const currentUser = getCurrentUser();
  const isTeamLead = currentUser?.roleCode === 'TEAM_LEAD' || currentUser?.role === 'team-lead';

  useEffect(() => {
    const syncRoleName = () => {
      setDisplayRoleName((prev) => {
        const next = authService.getStoredUserRoleName();
        return prev !== next ? next : prev;
      });
    };
    syncRoleName();
    window.addEventListener('hd:user-details-updated', syncRoleName);
    window.addEventListener('user-logged-in', syncRoleName);
    const interval = setInterval(syncRoleName, 500);
    return () => {
      window.removeEventListener('hd:user-details-updated', syncRoleName);
      window.removeEventListener('user-logged-in', syncRoleName);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async (loader, fn) => {
      loader(true);
      try {
        await fn();
      } finally {
        if (!cancelled) loader(false);
      }
    };

    run(setStatsLoading, fetchMyTicketsCount);
    run(setRatingLoading, fetchCustomerRating);
    run(setActivityLoading, fetchRecentActivity);
    if (isTeamLead) {
      run(setTeamOverviewLoading, fetchTeamOverview);
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamLead]);

  const filteredModalTickets = useMemo(() => {
    if (ticketListModal === 'resolvedToday') {
      return modalTickets;
    }
    let filtered = modalTickets;
    if (ticketModalStatusFilter !== 'all') {
      filtered = filtered.filter((t) => {
        const s = (t.status || '').toLowerCase();
        if (ticketModalStatusFilter === 'open') return s === 'open' || s === 'new';
        if (ticketModalStatusFilter === 'in-progress') {
          return s === 'in-progress' || s === 'in progress' || s === 'assigned' || s === 'inprogress';
        }
        if (ticketModalStatusFilter === 'resolved') return s === 'resolved';
        if (ticketModalStatusFilter === 'closed') return s === 'closed';
        return s === ticketModalStatusFilter.toLowerCase();
      });
    }
    const q = ticketModalSearchQuery.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter(
        (ticket) =>
          (ticket.subject || ticket.title || '').toLowerCase().includes(q) ||
          (ticket.description || '').toLowerCase().includes(q) ||
          String(ticket.id || '').toLowerCase().includes(q) ||
          (ticket.ticketNumber || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [modalTickets, ticketModalStatusFilter, ticketModalSearchQuery, ticketListModal]);

  useEffect(() => {
    if (!ticketListModal) return undefined;
    setTicketModalSearchQuery('');
    setTicketModalStatusFilter('all');
    setModalTickets([]);
    let cancelled = false;
    (async () => {
      setModalTicketsLoading(true);
      try {
        const response =
          ticketListModal === 'resolvedToday'
            ? await ticketService.getResolvedTodayTickets()
            : await ticketService.getMyTickets({ fetchAll: true });
        const responseData = response?.data;
        if (cancelled) return;
        if (!responseData?.success || !responseData?.data) {
          setModalTickets([]);
          return;
        }
        const raw = responseData.data.content || [];
        setModalTickets(raw.map(mapAgentTicketRow));
      } catch (err) {
        console.error('Error loading tickets for dashboard modal:', err);
        if (!cancelled) setModalTickets([]);
      } finally {
        if (!cancelled) setModalTicketsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketListModal]);

  const fetchMyTicketsCount = async () => {
    try {
      // Fetch statistics from the backend API for accurate counts
      const statsResponse = await ticketService.getMyTicketsStatistics();
      const statsData = statsResponse?.data;
      
      if (statsData?.success && statsData?.data) {
        const statistics = statsData.data;
        
        setMyTicketsCount(statistics.total || 0);
        setTicketStats({
          open: statistics.open || 0,
          pending: statistics.pending || 0,
          escalated: statistics.escalated || 0,
          resolved: statistics.resolved || 0,
          inProgress: statistics['in-progress'] || 0,
          resolvedToday: statistics.resolvedToday || 0,
          totalResolved: statistics.resolved || 0
        });
      }
    } catch (err) {
      console.error('Error fetching my tickets count:', err);
      // Keep default values (0) on error
    }
  };

  const fetchCustomerRating = async () => {
    try {
      const ratingResponse = await ticketService.getCustomerRating();
      const ratingData = ratingResponse?.data;
      
      if (ratingData?.success && ratingData?.data) {
        // Extract the rating value from the response
        // The API might return it as averageRating, rating, or directly as a number
        const rating = ratingData.data.averageRating || 
                      ratingData.data.rating || 
                      ratingData.data;
        
        // Ensure it's a number or null
        const numericRating = typeof rating === 'number' ? rating : 
                            typeof rating === 'string' ? parseFloat(rating) : null;
        
        setCustomerRating(numericRating);
      }
    } catch (err) {
      console.error('Error fetching customer rating:', err);
      // Keep null on error, will display 'N/A'
    }
  };

  const fetchTeamOverview = async () => {
    try {
      // Get teamId from userDetails in sessionStorage
      const userDetailsStr = sessionStorage.getItem('userDetails');
      if (!userDetailsStr) {
        console.warn('User details not found in sessionStorage');
        return;
      }
      
      const userDetails = JSON.parse(userDetailsStr);
      const teamId = userDetails.teamId || userDetails.team_id;
      
      if (!teamId) {
        console.warn('Team ID not found in user details');
        return;
      }
      
      const overviewResponse = await ticketService.getTeamAverageFeedback(teamId);
      const overviewData = overviewResponse?.data;
      
      if (overviewData?.success && overviewData?.data) {
        // Extract the average feedback value from the response
        // The API might return it as averageFeedback, feedback, or directly as a number
        const feedback = overviewData.data.averageRating || 0.0;

        
        setTeamOverview(feedback);
      }
    } catch (err) {
      console.error('Error fetching team overview:', err);
      // Keep null on error, will display 'N/A'
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const activityResponse = await ticketService.getMyRecentActivity(10);
      const activityData = activityResponse?.data;

      if (activityData?.success && Array.isArray(activityData?.data)) {
        setRecentActivity(activityData.data);
      } else {
        setRecentActivity([]);
      }
    } catch (err) {
      console.error('Error fetching recent activity:', err);
      setRecentActivity([]);
    }
  };

  fetchMyTicketsCountRef.current = fetchMyTicketsCount;
  fetchRecentActivityRef.current = fetchRecentActivity;
  fetchTeamOverviewRef.current = fetchTeamOverview;

  useEffect(() => {
    const onRealtimeRefresh = () => {
      fetchMyTicketsCountRef.current?.();
      fetchRecentActivityRef.current?.();
      if (isTeamLead) {
        fetchTeamOverviewRef.current?.();
      }
    };
    window.addEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
    return () => window.removeEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
  }, [isTeamLead]);

  const handleRecentActivityClick = (activity) => {
    const ticketId = activity?.ticketId;
    if (!ticketId) {
      return;
    }
    navigate(`/agent/tickets/${ticketId}`, { state: { mode: 'view' } });
  };

  const sessionUser = getCurrentUser();
  const welcomeName = (() => {
    try {
      const raw =
        typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userDetails') : null;
      if (raw) {
        const details = JSON.parse(raw);
        const full = (details?.fullName || details?.full_name || '').trim();
        if (full) return full;
        const composed = [details?.firstName, details?.lastName].filter(Boolean).join(' ').trim();
        if (composed) return composed;
      }
    } catch {
      /* ignore */
    }
    const fromSession =
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('userName')?.trim() : '';
    if (fromSession) return fromSession;
    return sessionUser?.username || sessionUser?.email || 'User';
  })();

  const roleLabel = displayRoleName || (isTeamLead ? 'Team Lead' : 'Agent');
  const operationsBadgeLabel = /\boperations\b/i.test(roleLabel)
    ? roleLabel
    : `${roleLabel} Operations & Support`;
  const dashboardTitle = `${roleLabel} Dashboard`;

  return (
    <div className="w-full min-w-0 bg-[#F5F7FA]">
      <div className="mx-auto w-full max-w-screen-2xl min-w-0 space-y-3 sm:space-y-4">
        {/* Page title + greeting — centered */}
        <header className="w-full rounded-xl border border-gray-200/80 bg-[#F8F9FA] px-4 py-3 sm:px-5 sm:py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
          <div className="mx-auto flex w-full flex-col items-center gap-1.5 text-center">
            <div
              className="inline-flex w-fit items-center px-2.5 py-0.5 rounded-full"
              style={{ backgroundColor: 'rgba(72, 61, 133, 0.1)', border: '1px solid rgba(72, 61, 133, 0.2)' }}
            >
              <span className="text-[11px] sm:text-xs font-semibold" style={{ color: '#483D85' }}>
                {operationsBadgeLabel}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-[#252525] dark:text-white">
              {dashboardTitle}
            </h1>
            <p className="text-xs sm:text-sm text-[#585858] dark:text-gray-300 leading-snug max-w-2xl px-1">
              Welcome back, <span className="font-medium text-gray-900 dark:text-gray-100">{welcomeName}</span>.{' '}
              {isTeamLead
                ? "Manage your team's tickets and track team performance."
                : 'Manage your tickets and track your performance.'}
            </p>
          </div>
        </header>

        <div className="w-full min-w-0 pb-1 space-y-3 sm:space-y-4">
          {/* Quick Stats */}
          <div
            className="grid w-full min-w-0 gap-2 sm:gap-3 justify-items-stretch [grid-template-columns:repeat(auto-fit,minmax(min(100%,13rem),1fr))]"
          >
            <Card
              className="cursor-pointer hover:shadow-md transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              role="button"
              tabIndex={0}
              aria-label="Open read-only list of my tickets"
              onClick={() => setTicketListModal('myTickets')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setTicketListModal('myTickets');
                }
              }}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">My Tickets</p>
                    <StatValue loading={statsLoading}>
                      <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{myTicketsCount}</p>
                    </StatValue>
                  </div>
                  <Ticket className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover:shadow-md transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
              role="button"
              tabIndex={0}
              aria-label="Open read-only list of tickets resolved today"
              onClick={() => setTicketListModal('resolvedToday')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setTicketListModal('resolvedToday');
                }
              }}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Resolved Today</p>
                    <StatValue loading={statsLoading}>
                      <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{ticketStats.resolvedToday}</p>
                    </StatValue>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Customer Rating</p>
                    <StatValue loading={ratingLoading}>
                      <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                        {customerRating !== null && customerRating !== undefined ? customerRating.toFixed(1) : 'N/A'}
                      </p>
                    </StatValue>
                  </div>
                  <Star className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            {/* {isTeamLead && (
              <Card>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Team Overview</p>
                      <StatValue loading={teamOverviewLoading}>
                        <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                          {teamOverview !== null && teamOverview !== undefined ? teamOverview.toFixed(1) : 'N/A'}
                        </p>
                      </StatValue>
                    </div>
                    <Users className="h-8 w-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            )} */}
          </div>

          <Modal
            isOpen={ticketListModal !== null}
            onClose={() => {
              setTicketListModal(null);
              setModalTickets([]);
              setTicketModalSearchQuery('');
              setTicketModalStatusFilter('all');
            }}
            title={ticketListModal === 'myTickets' ? 'My Tickets' : 'Resolved Today'}
            size="xl"
          >
            <div className="space-y-4">
              {ticketListModal === 'myTickets' && (
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search tickets by title, description, or ID..."
                    value={ticketModalSearchQuery}
                    onChange={(e) => setTicketModalSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={ticketModalStatusFilter} onValueChange={setTicketModalStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              )}

              {modalTicketsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading tickets...</p>
                </div>
              ) : filteredModalTickets.length === 0 ? (
                <div className="text-center py-8">
                  <Ticket className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 dark:text-gray-400">No tickets found</p>
                </div>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
                  {filteredModalTickets.map((ticket) => (
                    <Card key={String(ticket.id)} className="border border-gray-200 dark:border-gray-700">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <h3 className="font-semibold text-text-primary-light dark:text-text-primary-dark">
                                {ticket.ticketNumber}
                              </h3>
                              <Badge className={modalStatusBadgeClass(ticket.status)}>
                                {ticket.status}
                              </Badge>
                              <Badge className={modalPriorityBadgeClass(ticket.priority)}>
                                {String(ticket.priorityLabel || ticket.priority || 'medium')}
                              </Badge>
                            </div>
                            <p className="text-sm font-medium mb-1 text-text-primary-light dark:text-text-primary-dark">
                              {ticket.subject || ticket.title}
                            </p>
                            {ticket.description && (
                              <p className="text-sm mb-2 line-clamp-2 text-text-secondary-light dark:text-text-secondary-dark">
                                {ticket.description}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                              {ticket.createdAt && (
                                <span>Created: {formatModalDate(ticket.createdAt)}</span>
                              )}
                              {(ticket.lastUpdate || ticket.updatedAt) && (
                                <span>Updated: {formatModalDate(ticket.lastUpdate || ticket.updatedAt)}</span>
                              )}
                              {ticket.assignedTo && <span>Assigned to: {ticket.assignedTo}</span>}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Modal>

          {/* Recent activity | Ticket summary */}
          <div className="grid w-full min-w-0 min-h-0 grid-cols-1 gap-2 sm:gap-3 lg:grid-cols-2 lg:gap-3 items-stretch">
            <Card className="min-w-0 min-h-0 flex flex-col h-full">
              <CardHeader className="p-3 pb-2 shrink-0">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Activity className="h-5 w-5 shrink-0" />
                  <span>Recent Activity</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 flex-1 flex flex-col min-h-0">
                <div className="space-y-1.5 flex-1 min-h-0 overflow-y-auto max-h-[min(420px,50vh)] lg:max-h-none">
                  {activityLoading ? (
                    <div className="space-y-2 py-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-14 rounded-md bg-gray-100 dark:bg-gray-800 animate-pulse" />
                      ))}
                    </div>
                  ) : recentActivity.length === 0 ? (
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark py-4 text-center">
                      No recent activity
                    </p>
                  ) : null}
                  {!activityLoading && recentActivity.slice(0, 5).map((activity, index) => (
                    <div
                      key={activity.id || `${activity.ticketId || 'activity'}-${index}`}
                      className={`border border-border-light dark:border-border-dark rounded-md px-2 py-1.5 sm:px-2.5 sm:py-2 ${
                        activity.ticketId ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors' : ''
                      }`}
                      onClick={() => handleRecentActivityClick(activity)}
                      onKeyDown={(event) => {
                        if ((event.key === 'Enter' || event.key === ' ') && activity.ticketId) {
                          event.preventDefault();
                          handleRecentActivityClick(activity);
                        }
                      }}
                      role={activity.ticketId ? 'button' : undefined}
                      tabIndex={activity.ticketId ? 0 : -1}
                      title={activity.ticketId ? 'Open ticket details' : undefined}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0">
                          <div
                            className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                              activity.type === 'ticket_resolved'
                                ? 'bg-green-500'
                                : activity.type === 'ticket_escalated'
                                  ? 'bg-red-500'
                                  : 'bg-blue-500'
                            }`}
                          />
                          <div className="min-w-0">
                            <h3 className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark break-words leading-snug">
                              {activity.message || activity.description || 'Activity'}
                            </h3>
                            {(activity.ticketNumber || activity.ticket_number) && (
                              <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mt-0.5">
                                {activity.ticketNumber || activity.ticket_number}
                              </p>
                            )}
                            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
                              {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'Recently'}
                            </p>
                          </div>
                        </div>
                        <Badge
                          className={`shrink-0 text-[10px] sm:text-xs px-1.5 py-0 ${
                            activity.type === 'ticket_resolved'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : activity.type === 'ticket_escalated'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          }`}
                        >
                          {activity.type?.replace('_', ' ') || 'activity'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="min-w-0 min-h-0 flex flex-col h-full">
              <CardHeader className="p-3 pb-2 shrink-0">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Ticket className="h-5 w-5 shrink-0" />
                  <span>Ticket Summary</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 flex-1 flex flex-col">
                <div className="space-y-1.5 flex-1">
                  <div className="border border-border-light dark:border-border-dark rounded-md px-2 py-1.5 sm:px-2.5 sm:py-2">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <h3 className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">Open Tickets</h3>
                      <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 shrink-0 text-xs">
                        {statsLoading ? '—' : ticketStats.open}
                      </Badge>
                    </div>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark leading-snug">
                      Tickets currently assigned to you
                    </p>
                  </div>
                  <div className="border border-border-light dark:border-border-dark rounded-md px-2 py-1.5 sm:px-2.5 sm:py-2">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <h3 className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">Pending</h3>
                      <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 shrink-0 text-xs">
                        {statsLoading ? '—' : ticketStats.pending}
                      </Badge>
                    </div>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark leading-snug">
                      Tickets waiting for action
                    </p>
                  </div>
                  <div className="border border-border-light dark:border-border-dark rounded-md px-2 py-1.5 sm:px-2.5 sm:py-2">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <h3 className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">In Progress</h3>
                      <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 shrink-0 text-xs">
                        {statsLoading ? '—' : ticketStats.inProgress}
                      </Badge>
                    </div>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark leading-snug">
                      Tickets you're actively working on
                    </p>
                  </div>
                  <div className="border border-border-light dark:border-border-dark rounded-md px-2 py-1.5 sm:px-2.5 sm:py-2">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <h3 className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">Escalated</h3>
                      <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 shrink-0 text-xs">
                        {statsLoading ? '—' : ticketStats.escalated}
                      </Badge>
                    </div>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark leading-snug">
                      Tickets that require immediate attention
                    </p>
                  </div>
                  <div className="border border-border-light dark:border-border-dark rounded-md px-2 py-1.5 sm:px-2.5 sm:py-2">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <h3 className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">Resolved</h3>
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 shrink-0 text-xs">
                        {statsLoading ? '—' : ticketStats.resolved}
                      </Badge>
                    </div>
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark leading-snug">
                      All tickets you've resolved
                    </p>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-border-light dark:border-border-dark text-center shrink-0">
                  <Button size="sm" className="w-full sm:w-auto" onClick={() => navigate('/agent/tickets/my-tickets')}>
                    View All Tickets
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Performance Metrics */}
          {/* <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Target className="h-5 w-5" />
                <span>Performance Metrics</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center p-4 border border-border-light dark:border-border-dark rounded-lg">
                  <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                    {slaStatus.averageResolutionTime || 'N/A'}
                  </div>
                  <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">Avg Resolution Time</div>
                </div>
                <div className="text-center p-4 border border-border-light dark:border-border-dark rounded-lg">
                  <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                    {performance.customerSatisfaction || 0}%
                  </div>
                  <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">Customer Satisfaction</div>
                </div>
              </div>
            </CardContent>
          </Card> */}
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;
